/**
 * Builds the phone dial-code prefix for a country calling code.
 * Returns an empty string when no code is given.
 *
 * @param {string} dialCode -- the calling code without the plus (e.g. "55")
 * @returns {string} the prefix (e.g. "+55"), or "" when no code is given
 */
export const dialPrefix = (dialCode) => (dialCode ? `+${dialCode}` : '');

const dialCodeOf = (select) => select.selectedOptions[0]?.dataset.dialCode || '';

/**
 * Mirrors react-phone-input-2 on the server-rendered signup form: selecting a
 * country fills the phone field with that country's dial code, swapping the
 * previous prefix while keeping any national digits already typed. A
 * dial-code-only value is cleared on submit so the optional phone is not
 * persisted as a bare country code (which would collide on phone uniqueness).
 * For the same reason a required phone that carries a `data-required-message`
 * (the sign-up form) reports itself invalid while it holds only the prefix, so
 * the required-fields gate does not count it as filled; forms without the
 * message (the MFA notice) keep submitting and let the server answer. No-ops
 * on pages without the country dropdown or phone field.
 */
export const initPhoneDialCode = () => {
  const phoneCountry = document.getElementById('phone_country');
  const phone = document.getElementById('phone');
  if (!phoneCountry || !phone) return;

  let prefix = dialPrefix(dialCodeOf(phoneCountry));

  const syncRequiredValidity = () => {
    const { requiredMessage } = phone.dataset;
    if (!phone.required || !requiredMessage) return;

    const onlyDialCode = phone.value !== '' && phone.value === prefix;
    phone.setCustomValidity(onlyDialCode ? requiredMessage : '');
  };

  const applyPrefix = () => {
    const next = dialPrefix(dialCodeOf(phoneCountry));

    // Once the field no longer starts with the tracked prefix the user has
    // taken manual control of it (e.g. edited the dial code or browser
    // autofill replaced it), so leave their input intact rather than wiping it.
    if (phone.value && !phone.value.startsWith(prefix)) {
      prefix = next;
      return;
    }

    phone.value = next + phone.value.slice(prefix.length);
    prefix = next;
  };

  const applyPrefixAndValidity = () => {
    applyPrefix();
    syncRequiredValidity();
  };

  applyPrefixAndValidity();
  phoneCountry.addEventListener('change', applyPrefixAndValidity);
  phone.addEventListener('input', syncRequiredValidity);

  // A sign-up submit that only advances to the next in-page step (see
  // signup_step_transition.js) is not a real submission — guard on
  // defaultPrevented so an unmodified dial-code prefix survives to the step
  // where the phone field is actually shown.
  phone.form?.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;
    if (phone.value === prefix) phone.value = '';
  });
};
